import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Search, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useFlagList } from '@/services/scheduling/use-flag-list';
import type { AppointmentFormValues } from '@/types/appointment-form';
// PROD-2743 design sheet §3 (icons): DS names ON only (?ef=frameV2); OFF keeps today's lucide icon, unchanged.

interface FlagsPickerViewProps {
  /** Closes the view without persisting changes. */
  onCancel: () => void;
}

/**
 * FlagsPickerView — full panel-content replacement.
 * Mirrors Flutter `flags_selection_widget.dart`:
 *   - Top bar: Back / Flags / Save
 *   - Search input filters the flat catalog by display
 *   - Categories rendered as `FormView`-style sections; each row is a
 *     circular checkbox + display name
 *   - Save writes the selected list back to `patientFlags` + `flagIds`
 *
 * Selection is keyed by `code` (Flutter parity — `fg.code == flag.code`).
 */
export function FlagsPickerView({ onCancel }: FlagsPickerViewProps) {
  const { watch, setValue } = useFormContext<AppointmentFormValues>();
  const seedFlags = watch('patientFlags');

  // Local selection — keyed by code, seeded from the form on mount.
  const [selectedCodes, setSelectedCodes] = useState<Set<string>>(() => {
    const init = new Set<string>();
    seedFlags?.forEach((f) => {
      const c = String(f.code ?? '').trim();
      if (c) init.add(c);
    });
    return init;
  });

  // Re-seed if the form's patientFlags changes after mount (e.g. a fresh
  // PreBookSelectedPatient lands while the picker is open).
  useEffect(() => {
    const next = new Set<string>();
    seedFlags?.forEach((f) => {
      const c = String(f.code ?? '').trim();
      if (c) next.add(c);
    });
    setSelectedCodes(next);
    // We intentionally only re-run when the form list reference changes —
    // local toggles must not be clobbered.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedFlags]);

  const [searchText, setSearchText] = useState('');

  const { data: catalog = [], isLoading } = useFlagList();

  const filteredCatalog = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return catalog;
    return catalog
      .map((cat) => ({
        ...cat,
        flagResponse: (cat.flagResponse ?? []).filter((f) =>
          (f.display ?? '').toLowerCase().includes(q),
        ),
      }))
      .filter((cat) => (cat.flagResponse?.length ?? 0) > 0);
  }, [catalog, searchText]);

  const toggle = (code: string) => {
    setSelectedCodes((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const onSave = () => {
    // Walk the catalog so we capture display + categoryName for the
    // card, not just bare ids. Selection set is the source of truth.
    const picked: AppointmentFormValues['patientFlags'] = [];
    const seen = new Set<string>();
    for (const cat of catalog) {
      for (const f of cat.flagResponse ?? []) {
        const code = String(f.code ?? '').trim();
        if (!code || !selectedCodes.has(code) || seen.has(code)) continue;
        seen.add(code);
        picked.push({
          code: f.code,
          display: f.display,
          categoryName: cat.categoryName,
        });
      }
    }
    setValue('patientFlags', picked, { shouldValidate: true });
    setValue(
      'flagIds',
      picked.map((f) => String(f.code ?? '')),
    );
    onCancel();
  };

  return (
    <div className="flex flex-col h-full">
      {/* Top bar */}
      <div className="booking-subhead" data-slot="booking-subhead">
        <button
          onClick={onCancel}
          className="text-sm font-medium text-brand-primary hover:text-interactive-hover"
        >
          Back
        </button>
        <h2 className="booking-subhead-title">Flags</h2>
        <button
          type="button"
          onClick={onSave}
          className="text-sm font-medium text-brand-primary hover:text-interactive-hover"
        >
          Save
        </button>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-hide">
        <div className="px-4 py-4 space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-3 size-4 text-text-hint pointer-events-none" />
            <input
              placeholder="Search flags"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="w-full h-10 pl-10 pr-3 rounded-lg border border-border-default bg-grey-100 text-sm text-text-primary placeholder:text-text-hint focus:border-brand-primary focus:bg-surface-card focus:outline-none"
            />
          </div>

          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-10 rounded-lg bg-grey-200 animate-pulse" />
              ))}
            </div>
          ) : filteredCatalog.length === 0 ? (
            <p className="text-sm text-text-muted text-center py-6">
              No flags match.
            </p>
          ) : (
            filteredCatalog.map((cat, i) => (
              <div key={cat.categoryName ?? `cat-${i}`}>
                <p className="text-xs font-medium text-text-muted mb-2">
                  {cat.categoryName ?? '—'}
                </p>
                <div className="rounded-lg border border-border-default bg-surface-card divide-y divide-border-subtle overflow-hidden">
                  {(cat.flagResponse ?? []).map((flag) => {
                    const code = String(flag.code ?? '').trim();
                    const isSelected = code ? selectedCodes.has(code) : false;
                    return (
                      <button
                        key={flag.id || code || flag.display}
                        type="button"
                        onClick={() => code && toggle(code)}
                        className="flex items-center gap-3 w-full px-3 py-3 text-left hover:bg-grey-100 transition-colors"
                      >
                        <span
                          className={cn(
                            'flex items-center justify-center w-5 h-5 rounded-full border-2 flex-shrink-0',
                            isSelected
                              ? 'bg-brand-primary border-brand-primary'
                              : 'border-border-strong bg-surface-card',
                          )}
                        >
                          {isSelected ? <Check className="size-3 text-white" /> : null}
                        </span>
                        <span className="flex-1 text-sm text-text-primary">
                          {flag.display}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
